import { useRef } from 'react'
import { CheckCircle2, Circle, CircleDot, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { checklistSummary, tourStatus } from '@/src/lib/onboarding/tour-machine'
import { useOnboarding } from './Onboarding'

const STATUS_LABEL = { done: 'Done', started: 'Started', new: 'Not started' } as const

/**
 * The Getting started checklist: the tours for this person's role, what they
 * have done, and a way to start, carry on or retake each one.
 */
export function GettingStarted({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { tours, progress, start } = useOnboarding()
  const { done, total } = checklistSummary(progress, tours)
  // Starting a tour hands focus to the tour's card, not back to Help.
  const starting = useRef(false)

  function begin(tourId: string, resume = false) {
    starting.current = true
    start(tourId, { resume })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90svh] overflow-y-auto"
        onCloseAutoFocus={(event) => {
          if (!starting.current) return
          starting.current = false
          event.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>Getting started</DialogTitle>
          <DialogDescription>
            Short tours of the work you will do most in Helm. They point at the real screens and
            never save anything for you. Fields with an info icon say what they are for.
          </DialogDescription>
        </DialogHeader>

        {total > 0 ? (
          <>
            <div className="space-y-1.5">
              <p className="text-sm font-medium">
                {done} of {total} done
              </p>
              <Progress value={(done / total) * 100} aria-label="Tours done" />
            </div>
            <ul className="divide-y rounded-xl border" aria-label="Tours">
              {tours.map((tour) => {
                const status = tourStatus(progress, tour.id)
                const lastStep = progress.tours[tour.id]?.lastStep
                return (
                  <li key={tour.id} className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
                    <div className="flex min-w-0 flex-1 gap-3">
                      {status === 'done' ? (
                        <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
                      ) : status === 'started' ? (
                        <CircleDot
                          className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                          aria-hidden
                        />
                      ) : (
                        <Circle
                          className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                          aria-hidden
                        />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium">
                          {tour.title}
                          <span className="sr-only">, {STATUS_LABEL[status]}</span>
                        </p>
                        <p className="text-sm text-muted-foreground">{tour.summary}</p>
                        <p className="text-xs text-muted-foreground">
                          {tour.steps.length} steps
                          {status === 'started' && lastStep !== undefined
                            ? ` · stopped at step ${lastStep + 1}`
                            : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2 pl-8 sm:pl-0">
                      {status === 'started' && (
                        <Button size="sm" onClick={() => begin(tour.id, true)}>
                          Resume<span className="sr-only"> {tour.title}</span>
                        </Button>
                      )}
                      {status === 'new' ? (
                        <Button size="sm" onClick={() => begin(tour.id)}>
                          Start<span className="sr-only"> {tour.title}</span>
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => begin(tour.id)}>
                          <RotateCcw />
                          Restart<span className="sr-only"> {tour.title}</span>
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">There are no tours for your account yet.</p>
        )}

        <DialogFooter className="items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">Reopen this any time from Help.</p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
